import { memo, createRef, PureComponent } from 'react';
import type { RefObject } from 'react';
import { withTheme } from 'styled-components';
import calculateMidColor, { hexToRgbA } from '@utilities/colors';
import { SnowflakeCanvasWrapper } from './styled';
import Labels from '@/components/Snowflake/Labels';
import type { WithThemeProps } from '@simplywallst/ui-core';

const MemoLabels = memo(Labels);

export type SnowflakeSection =
  | 'value'
  | 'future'
  | 'past'
  | 'health'
  | 'dividend';

export interface SnowflakeCanvasProps {
  dark?: boolean;
  width?: number;
  light?: boolean;
  points: number[];
  disabled?: boolean;
  disableLabel?: boolean;
  highlightSection?: SnowflakeSection;
}
interface State {
  canvasWidth: number;
}

interface Point {
  x: number;
  y: number;
}

class SnowflakeCanvas extends PureComponent<
  SnowflakeCanvasProps & WithThemeProps,
  State
> {
  static defaultProps = {
    dark: false,
    light: false,
    disabled: false,
    disableLabel: false,
  };

  //	Blob width with respect to the canvas width, if labels are
  //	active (aka if labels are visible, the blob width will equal
  //	0.7766785714 of the full width of the canvas)
  private widthRatio = 0.7766785714;
  private canvas: undefined | RefObject<HTMLCanvasElement>;
  private container: undefined | RefObject<HTMLDivElement>;
  private sections: SnowflakeSection[] = [
    'value',
    'future',
    'past',
    'health',
    'dividend',
  ];

  state = {
    canvasWidth: 0,
  };

  constructor(props) {
    super(props);
    this.canvas = createRef();
    this.container = createRef();
  }

  componentDidMount() {
    const { setCanvasWidthAndPaint } = this;
    try {
      window.addEventListener('resize', setCanvasWidthAndPaint);
      setCanvasWidthAndPaint();
    } catch (error) {
      console.error(error);
    }
  }

  componentWillUnmount() {
    const { setCanvasWidthAndPaint } = this;
    window.removeEventListener('resize', setCanvasWidthAndPaint);
  }

  componentDidUpdate() {
    try {
      this.paintCanvas();
    } catch (error) {
      console.error(error);
    }
  }

  setCanvasWidthAndPaint = () => {
    if (typeof this.container === 'undefined') return;
    const container = this.container.current;
    const { width } = this.props;
    const { canvasWidth } = this.state;

    if (container === null) return false;
    const realContainerWidth =
      container.offsetWidth > container.getBoundingClientRect().width
        ? Math.floor(container.getBoundingClientRect().width)
        : container.offsetWidth;

    if (width === undefined && canvasWidth === realContainerWidth) return false;
    this.setState({
      canvasWidth:
        (width !== undefined && width > 0 ? width : realContainerWidth) * 2,
    });
  };

  // Calculates the bezier control points for segment (x1,y1)-(x2,y2)
  calculateControlPoints = (x0, y0, x1, y1, x2, y2, x3, y3, tensor) => {
    const len1: number = Math.sqrt(
      (x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0)
    );
    const len2: number = Math.sqrt(
      (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1)
    );
    const len3: number = Math.sqrt(
      (x3 - x2) * (x3 - x2) + (y3 - y2) * (y3 - y2)
    );

    const k1: number = len1 / (len1 + len2);
    const k2: number = len2 / (len2 + len3);

    const xc1: number = (x0 + x1) / 2.0;
    const yc1: number = (y0 + y1) / 2.0;
    const xc2: number = (x1 + x2) / 2.0;
    const yc2: number = (y1 + y2) / 2.0;
    const xc3: number = (x2 + x3) / 2.0;
    const yc3: number = (y2 + y3) / 2.0;

    const xm1: number = xc1 + (xc2 - xc1) * k1;
    const ym1: number = yc1 + (yc2 - yc1) * k1;

    const xm2: number = xc2 + (xc3 - xc2) * k2;
    const ym2: number = yc2 + (yc3 - yc2) * k2;

    const cx1: number = xm1 + (xc2 - xm1) * tensor + x1 - xm1;
    const cy1: number = ym1 + (yc2 - ym1) * tensor + y1 - ym1;

    const cx2: number = xm2 + (xc2 - xm2) * tensor + x2 - xm2;
    const cy2: number = ym2 + (yc2 - ym2) * tensor + y2 - ym2;

    return [cx1, cy1, cx2, cy2] as number[];
  };

  bullsEyeFill = (isEven: boolean) => {
    const {
      theme: { color },
      dark,
      light,
      disableLabel,
    } = this.props;

    /**
     * Temporary hardcode for even and odd rings colours until a better
     * design can be achieve by changing chart implementation
     */

    if (isEven) {
      if (disableLabel) {
        return '#27313D';
      } else {
        return light || dark ? color.spaceGray : color.snowflakeRingEven;
      }
    } else {
      if (disableLabel) {
        return '#344352';
      } else {
        if (light) {
          return 'white';
        } else {
          return dark ? color.darkerGray : color.showflakeRingOdd;
        }
      }
    }
  };

  highlightSection = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ) => {
    const { highlightSection, disableLabel } = this.props;
    if (!highlightSection) return;

    //Create temporary canvas to copy to
    let tempCanvas: HTMLCanvasElement | null = document.createElement('canvas');
    const { width, height } = canvas;
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return;
    //Clip the highlighted section
    const PI = Math.PI;
    const diameter = width * (disableLabel ? 0.95 : this.widthRatio);
    const extraPadding = 12; //Extra radius of the circle to cover blobs that overflow the edges
    const index = this.sections.indexOf(highlightSection);
    const startAngle = -PI / 2 + ((2 * PI) / 5) * (index - 0.5);
    const endAngle = -PI / 2 + ((2 * PI) / 5) * (index + 0.5);
    const center = [width / 2, height / 2];
    tempCtx.beginPath();
    tempCtx.arc(
      center[0],
      center[1],
      diameter / 2 + extraPadding,
      startAngle,
      endAngle
    );
    tempCtx.lineTo(center[0], center[1]);
    tempCtx.closePath();
    tempCtx.clip();
    //Scale the highlighted section
    tempCtx.translate(center[0], center[1]);
    tempCtx.scale(1.05, 1.05);
    tempCtx.translate(-center[0], -center[1]);
    //Draw scaled section
    tempCtx.drawImage(canvas, 0, 0);

    //Darken the rest of the circle in original image
    ctx.save();
    ctx.fillStyle = 'rgb(0,0,0,0.4)';
    ctx.beginPath();
    ctx.arc(
      center[0],
      center[1],
      diameter / 2 + extraPadding,
      endAngle,
      startAngle
    );
    ctx.globalCompositeOperation = 'source-atop';
    ctx.lineTo(center[0], center[1]);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    //Draw the highlighted section back in with shadows
    ctx.save();
    ctx.shadowBlur = 8;
    ctx.shadowColor = 'rgb(0,0,0,0.4)';
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.restore();

    // Cleans up canvas element and variable that is no longer used, prevents memory leak
    tempCanvas.width = 0;
    tempCanvas.height = 0;
    try {
      tempCanvas.remove();
    } catch (e) {
      document.body?.removeChild(tempCanvas);
    }
    tempCanvas = null;
  };

  paintCanvas = () => {
    if (typeof this.canvas === 'undefined') return;
    const canvas = this.canvas.current;
    const { calculateControlPoints, widthRatio, bullsEyeFill } = this;
    const {
      points = [1, 1, 1, 1, 1],
      theme: { flakeMinColour, flakeMaxColour, color },
      dark,
      light,
      disabled,
      disableLabel,
    } = this.props;
    const ctx = canvas && canvas.getContext('2d');
    if (canvas && ctx && points) {
      const width = canvas.width;
      const height = canvas.height;
      const axisLineWidth = 3 / 280;
      const PI = Math.PI;
      const diameter = width * (disableLabel ? 0.95 : widthRatio);
      const arr: Point[] = [];
      const nodeCount = points.length;
      const maxSum = 30;
      const minSum = 5;
      const sum: number = points.reduce((a, b) => +a + (+b - 1), 0);
      const colour = 1 - (sum - minSum) / (maxSum - minSum);
      // null check;
      if (nodeCount === 0) return;
      let max = 7;
      let flakeColorValue = 0;
      //
      flakeColorValue = Math.max(colour, 0);
      flakeColorValue = Math.min(colour, 1);
      //
      for (let i = 0; i < nodeCount; i++) {
        max = Math.max(max, points[i]);
      }
      //
      for (let i = 0; i < nodeCount; i++) {
        const radius = ((diameter / 2) * points[i]) / max;
        const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
        const x = width / 2 + Math.cos(angle) * radius;
        const y = width / 2 + Math.sin(angle) * radius;
        arr.push({ x: x, y: y });
      }
      //
      ctx.clearRect(0, 0, width, height);
      //
      ctx.translate(0, 0);
      //	Draw background arc
      // ctx.beginPath();
      // ctx.fillStyle = '#27313D';
      // ctx.arc(width / 2, height / 2, width / 2 - 4, 0, 2 * PI);
      // ctx.fill();
      //	Draw bullseye arcs
      for (let i = max; i > 1; i--) {
        ctx.beginPath();
        ctx.fillStyle = bullsEyeFill(i % 2 !== 0);
        ctx.arc(width / 2, height / 2, (i / max) * (diameter / 2), 0, 2 * PI);
        ctx.fill();
      }
      //	Draw axis lines
      for (let i = 5; i > 0; i--) {
        const radius = diameter / 2;
        const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
        const x = width / 2 + Math.cos(angle) * radius;
        const y = width / 2 + Math.sin(angle) * radius;
        ctx.beginPath();
        ctx.strokeStyle = light
          ? 'white'
          : dark
          ? color.darkerGray
          : color.showflakeRingOdd;
        ctx.lineWidth = width * axisLineWidth;
        ctx.moveTo(width / 2, height / 2);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      //	Draw blob
      ctx.beginPath();
      ctx.strokeStyle = disabled
        ? color.brand03RGBA(0.8)
        : calculateMidColor(flakeMaxColour, flakeMinColour, flakeColorValue, 1);
      ctx.fillStyle = disabled
        ? hexToRgbA(color.brand01, 0.5)
        : calculateMidColor(
            flakeMaxColour,
            flakeMinColour,
            flakeColorValue,
            0.7
          );
      ctx.lineWidth = 4;
      ctx.moveTo(arr[0].x, arr[0].y);
      let bezier;
      let x0, y0, x1, y1, x2, y2, x3, y3;
      for (let i = 0; i < nodeCount; i++) {
        x0 = arr[(i - 1 + nodeCount) % nodeCount].x;
        y0 = arr[(i - 1 + nodeCount) % nodeCount].y;
        x1 = arr[i].x;
        y1 = arr[i].y;
        x2 = arr[(i + 1) % nodeCount].x;
        y2 = arr[(i + 1) % nodeCount].y;
        x3 = arr[(i + 2) % nodeCount].x;
        y3 = arr[(i + 2) % nodeCount].y;
        bezier = calculateControlPoints(x0, y0, x1, y1, x2, y2, x3, y3, 1);
        ctx.bezierCurveTo(
          bezier[0],
          bezier[1],
          bezier[2],
          bezier[3],
          arr[(i + 1) % nodeCount].x,
          arr[(i + 1) % nodeCount].y
        );
      }
      ctx.fill();
      ctx.stroke();
      //	Draw labels or dots
      if (disableLabel) {
        for (let i = 0; i < nodeCount; i++) {
          const radius = diameter / 2;
          const angle = -PI / 2 + ((2 * PI) / nodeCount) * i;
          const x = width / 2 + Math.cos(angle) * radius;
          const y = width / 2 + Math.sin(angle) * radius;
          ctx.beginPath();
          ctx.fillStyle = 'white';
          ctx.arc(x, y, 0.02 * width, 0, 2 * PI);
          ctx.fill();
        }
      }

      //Do any additional modifications
      this.highlightSection(canvas, ctx);
      //
      ctx.save();
      ctx.restore();
    }
  };

  render() {
    const { canvasWidth } = this.state;
    const { disableLabel, highlightSection } = this.props;
    return (
      <SnowflakeCanvasWrapper ref={this.container}>
        <canvas
          ref={this.canvas}
          width={canvasWidth}
          height={canvasWidth}
          style={{
            width: `${canvasWidth / 2}px`,
            height: `${canvasWidth / 2}px`,
          }}
        />
        {!disableLabel && (
          <MemoLabels
            dimension={canvasWidth / 2}
            highlightSection={highlightSection}
          />
        )}
      </SnowflakeCanvasWrapper>
    );
  }
}

export default withTheme(SnowflakeCanvas) as React.ComponentType<
  Omit<SnowflakeCanvasProps, 'theme'>
>;
